import classnames from 'classnames';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';

import { userHasPaidTier } from '@wetransfer/transfer/helper/user';
import Analytics from '@wetransfer/transfer/modules/analytics';
import I18n from '@wetransfer/transfer/modules/i18n';
import { setDropZoneState } from '@wetransfer/transfer/store/action/layout';
import { allowedSizeSelector } from '@wetransfer/transfer/store/selector/transfers';
import { State } from '@wetransfer/transfer/store/types';
import { addFiles } from '@wetransfer/transfer/transfer-bundle/store/actions';
import { getCurrentSize } from '@wetransfer/transfer/transfer-bundle/store/selectors';

import './DropZone.scss';

export interface Props {
  onDropAccepted(files: File[]): void;
  onDragLeave(): void;
  isPro: boolean;
  dropzoneActive?: boolean;
}

export function DropZone({
  onDropAccepted,
  onDragLeave,
  isPro,
  dropzoneActive = true,
}: Props): JSX.Element {
  const onDrop = React.useCallback(
    (acceptedFiles) => {
      const withRelativePath = Array.from(acceptedFiles).map(
        (file: File & { relativePath: string; path: string }) => {
          // The new dropzone returns the file path a bit different and adds a slash in the front,
          // where the rest of the file adding logic expects a relativepath without a forward slash in the start.
          file.relativePath = file.path.startsWith('/')
            ? file.path.substring(1)
            : file.path;

          return file;
        }
      );

      onDropAccepted(withRelativePath);
      if (onDragLeave) onDragLeave();
    },
    [onDropAccepted, onDragLeave]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDragLeave,
  });

  return (
    <div
      {...getRootProps({
        className: classnames('DropZone', {
          'DropZone--active': isDragActive || dropzoneActive,
          'DropZone--pro': isPro,
          'DropZone--default': !isPro,
        }),
      })}
    >
      <input {...getInputProps()} />
      <h3 className="DropZone__title">{I18n.t('dropzone.title')}</h3>
      <p className="DropZone__text">{I18n.t('dropzone.text')}</p>
    </div>
  );
}

export default function ConnectedDropZone({
  handleDrop,
  disabled,
}: {
  handleDrop?: (files: File[]) => void;
  disabled?: boolean;
}): JSX.Element {
  const dispatch = useDispatch();

  const {
    transfer,
    allowedSize,
    maximumNumberOfFiles,
    isPro,
    dropzoneActive,
    currentSize,
  } = useSelector((state: State) => {
    const {
      transfer,
      user,
      layout: { dropzoneActive },
    } = state;

    return {
      transfer,
      allowedSize: allowedSizeSelector(state),
      user,
      isPro: userHasPaidTier(user),
      maximumNumberOfFiles: user.rules.maximumNumberOfFiles,
      dropzoneActive,
      currentSize: getCurrentSize(state),
    };
  });

  function handleOnAcceptedDrop(files: File[]) {
    Analytics.trackGoogleEvent({
      category: 'Transfer',
      action: 'File Added',
      label: 'dropzone',
    });

    if (handleDrop) {
      handleDrop(files);
    } else {
      dispatch(
        addFiles({
          transfer,
          files,
          allowedSize,
          currentSize,
          maximumNumberOfFiles,
        })
      );
    }
  }

  const onDragLeave = React.useCallback(() => {
    if (dropzoneActive) {
      dispatch(setDropZoneState(false));
    }
  }, [dispatch, dropzoneActive]);

  // for Pro backgrounds that don't receive event from wallpaper client
  React.useEffect(() => {
    const handleDrag = () => {
      if (!dropzoneActive) {
        dispatch(setDropZoneState(true));
      }
    };
    document.body.addEventListener('dragover', handleDrag, false);
    document.body.addEventListener('drop', onDragLeave, false);

    return () => {
      document.body.removeEventListener('dragover', handleDrag, false);
      document.body.addEventListener('drop', onDragLeave, false);
    };
  }, [dispatch, dropzoneActive, onDragLeave]);

  if (disabled) return null;

  return (
    <DropZone
      onDropAccepted={handleOnAcceptedDrop}
      onDragLeave={onDragLeave}
      isPro={isPro}
      dropzoneActive={dropzoneActive}
    />
  );
}
